import React, { useState, useEffect, useCallback } from "react";
import { Bot, Plus, Trash2, Edit3, Save, X, MessageSquare, BookOpen, Loader2, ChevronDown, ChevronUp, Search, HelpCircle, Info, Package } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_META = {
  faq: { label: "FAQ", icon: HelpCircle, color: "text-[#E83E8C]", bg: "bg-[#E83E8C]/10" },
  info: { label: "Info", icon: Info, color: "text-blue-600", bg: "bg-blue-50" },
  process: { label: "Process", icon: BookOpen, color: "text-teal-600", bg: "bg-teal-50" },
  package: { label: "Package", icon: Package, color: "text-[#FF9F1C]", bg: "bg-[#FF9F1C]/10" },
};

const BotTrainingTab = ({ auth, authAxios }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [searchQ, setSearchQ] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [addCategory, setAddCategory] = useState("faq");
  const [addData, setAddData] = useState({ question: "", answer: "", topic: "", content: "" });
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const fetchItems = useCallback(async () => {
    try {
      const r = await authAxios.get(`${API_URL}/api/admin/bot-knowledge`);
      setItems(r.data || []);
    } catch (e) { console.debug('[BotTraining] Fetch failed:', e.message); } finally { setLoading(false); }
  }, [authAxios]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleAdd = async () => {
    setSaving(true);
    try {
      await authAxios.post(`${API_URL}/api/admin/bot-knowledge`, { category: addCategory, ...addData });
      setAddData({ question: "", answer: "", topic: "", content: "" });
      setShowAdd(false);
      await fetchItems();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to add");
    } finally { setSaving(false); }
  };

  const handleUpdate = async (id) => {
    setSaving(true);
    try {
      await authAxios.put(`${API_URL}/api/admin/bot-knowledge/${id}`, editData);
      setEditingId(null);
      await fetchItems();
    } catch (e) { console.debug('[BotTraining] Update failed:', e.message); } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this knowledge item?")) return;
    try {
      await authAxios.delete(`${API_URL}/api/admin/bot-knowledge/${id}`);
      await fetchItems();
    } catch (e) { console.debug('[BotTraining] Delete failed:', e.message); }
  };

  const handleToggle = async (item) => {
    try {
      await authAxios.put(`${API_URL}/api/admin/bot-knowledge/${item.id}`, { active: !item.active });
      await fetchItems();
    } catch (e) { console.debug('[BotTraining] Toggle failed:', e.message); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-[#E83E8C]" size={32} /></div>;

  const filtered = items
    .filter(i => filter === "all" || i.category === filter)
    .filter(i => {
      if (!searchQ) return true;
      const q = searchQ.toLowerCase();
      return (i.question || "").toLowerCase().includes(q) || (i.answer || "").toLowerCase().includes(q) || (i.topic || "").toLowerCase().includes(q) || (i.content || "").toLowerCase().includes(q);
    });

  const faqCount = items.filter(i => i.category === "faq").length;
  const infoCount = items.filter(i => i.category !== "faq").length;

  return (
    <div className="space-y-5" data-testid="bot-training-tab">
      {/* Header Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <p className="text-xs text-[#94a3b8] uppercase tracking-wider">Total Items</p>
          <p className="text-2xl font-bold text-[#1E1B4B]">{items.length}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <p className="text-xs text-[#94a3b8] uppercase tracking-wider">FAQs</p>
          <p className="text-2xl font-bold text-[#E83E8C]">{faqCount}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <p className="text-xs text-[#94a3b8] uppercase tracking-wider">Knowledge</p>
          <p className="text-2xl font-bold text-blue-600">{infoCount}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <p className="text-xs text-[#94a3b8] uppercase tracking-wider">Active</p>
          <p className="text-2xl font-bold text-emerald-600">{items.filter(i => i.active).length}</p>
        </div>
      </div>

      {/* Actions Bar */}
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={() => setShowAdd(!showAdd)} data-testid="add-knowledge-btn"
          className="px-4 py-2 bg-[#E83E8C] text-white rounded-lg font-medium text-sm hover:bg-[#d63384] transition flex items-center gap-1.5">
          <Plus size={14} /> Add Knowledge
        </button>
        <div className="flex gap-1 bg-white rounded-lg border border-gray-200 p-1">
          {[{ val: "all", label: "All" }, { val: "faq", label: "FAQs" }, { val: "info", label: "Info" }, { val: "process", label: "Process" }].map(f => (
            <button key={f.val} onClick={() => setFilter(f.val)}
              className={`px-3 py-1 rounded-md text-xs font-medium transition ${filter === f.val ? "bg-[#E83E8C] text-white" : "text-[#475569] hover:bg-gray-50"}`}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94a3b8]" />
          <input type="text" value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Search knowledge..."
            className="w-full pl-8 pr-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-[#E83E8C] outline-none" />
        </div>
      </div>

      {/* Add Form */}
      {showAdd && (
        <div className="bg-white rounded-xl p-5 border-2 border-[#E83E8C]/30 shadow-sm" data-testid="add-knowledge-form">
          <h3 className="font-semibold text-[#1E1B4B] mb-3 flex items-center gap-2"><Bot size={16} /> New Knowledge Item</h3>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-[#94a3b8] mb-1 block">Category</label>
              <select value={addCategory} onChange={e => setAddCategory(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-[#E83E8C] outline-none">
                <option value="faq">FAQ (Question & Answer)</option>
                <option value="info">General Info</option>
                <option value="process">Process / How-to</option>
                <option value="package">Package Details</option>
              </select>
            </div>
            {addCategory === "faq" ? (
              <>
                <div>
                  <label className="text-xs text-[#94a3b8] mb-1 block">Question *</label>
                  <input type="text" value={addData.question} onChange={e => setAddData({ ...addData, question: e.target.value })}
                    placeholder="e.g. What time does the team arrive?" className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-[#E83E8C] outline-none" data-testid="add-faq-question" />
                </div>
                <div>
                  <label className="text-xs text-[#94a3b8] mb-1 block">Answer *</label>
                  <textarea value={addData.answer} onChange={e => setAddData({ ...addData, answer: e.target.value })} rows={3}
                    placeholder="The exact answer the bot should give..." className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-[#E83E8C] outline-none resize-y" data-testid="add-faq-answer" />
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="text-xs text-[#94a3b8] mb-1 block">Topic *</label>
                  <input type="text" value={addData.topic} onChange={e => setAddData({ ...addData, topic: e.target.value })}
                    placeholder="e.g. Setup process details" className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-[#E83E8C] outline-none" />
                </div>
                <div>
                  <label className="text-xs text-[#94a3b8] mb-1 block">Content *</label>
                  <textarea value={addData.content} onChange={e => setAddData({ ...addData, content: e.target.value })} rows={4}
                    placeholder="Detailed content the bot should know..." className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-[#E83E8C] outline-none resize-y" />
                </div>
              </>
            )}
            <div className="flex gap-2">
              <button onClick={handleAdd} disabled={saving}
                className="px-4 py-2 bg-[#E83E8C] text-white rounded-lg text-sm font-medium hover:bg-[#d63384] disabled:opacity-50 flex items-center gap-1.5">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
              </button>
              <button onClick={() => setShowAdd(false)} className="px-4 py-2 bg-gray-100 text-[#475569] rounded-lg text-sm hover:bg-gray-200">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Knowledge Items */}
      <div className="space-y-2">
        {filtered.length === 0 && <p className="text-center text-[#94a3b8] py-8">No knowledge items found</p>}
        {filtered.map(item => {
          const meta = CATEGORY_META[item.category] || CATEGORY_META.info;
          const Icon = meta.icon;
          const isEditing = editingId === item.id;
          const isExpanded = expandedId === item.id;

          return (
            <div key={item.id} className={`bg-white rounded-xl border shadow-sm overflow-hidden transition ${item.active ? "border-gray-100" : "border-gray-200 opacity-60"}`}>
              <div className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 transition"
                onClick={() => setExpandedId(isExpanded ? null : item.id)}>
                <span className={`p-1.5 rounded-lg ${meta.bg}`}><Icon size={14} className={meta.color} /></span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#1E1B4B] truncate">
                    {item.category === "faq" ? item.question : item.topic}
                  </p>
                  <p className="text-xs text-[#94a3b8]">{meta.label}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button onClick={(e) => { e.stopPropagation(); handleToggle(item); }}
                    className={`w-9 h-5 rounded-full transition-colors ${item.active ? "bg-emerald-500" : "bg-gray-300"}`}>
                    <div className={`w-4 h-4 rounded-full bg-white shadow transition-transform ${item.active ? "translate-x-4" : "translate-x-0.5"}`} />
                  </button>
                  {isExpanded ? <ChevronUp size={14} className="text-[#94a3b8]" /> : <ChevronDown size={14} className="text-[#94a3b8]" />}
                </div>
              </div>
              {isExpanded && (
                <div className="px-4 pb-4 border-t border-gray-100 pt-3">
                  {isEditing ? (
                    <div className="space-y-2">
                      {item.category === "faq" ? (
                        <>
                          <input type="text" value={editData.question || ""} onChange={e => setEditData({ ...editData, question: e.target.value })}
                            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" placeholder="Question" />
                          <textarea value={editData.answer || ""} onChange={e => setEditData({ ...editData, answer: e.target.value })} rows={3}
                            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm resize-y" placeholder="Answer" />
                        </>
                      ) : (
                        <>
                          <input type="text" value={editData.topic || ""} onChange={e => setEditData({ ...editData, topic: e.target.value })}
                            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" placeholder="Topic" />
                          <textarea value={editData.content || ""} onChange={e => setEditData({ ...editData, content: e.target.value })} rows={4}
                            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm resize-y" placeholder="Content" />
                        </>
                      )}
                      <div className="flex gap-2">
                        <button onClick={() => handleUpdate(item.id)} disabled={saving}
                          className="px-3 py-1.5 bg-[#E83E8C] text-white rounded-lg text-xs font-medium flex items-center gap-1">
                          {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
                        </button>
                        <button onClick={() => setEditingId(null)} className="px-3 py-1.5 bg-gray-100 text-[#475569] rounded-lg text-xs"><X size={12} /> Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm text-[#475569] whitespace-pre-wrap">{item.category === "faq" ? item.answer : item.content}</p>
                      <div className="flex gap-2 mt-3">
                        <button onClick={() => { setEditingId(item.id); setEditData(item.category === "faq" ? { question: item.question, answer: item.answer } : { topic: item.topic, content: item.content }); }}
                          className="px-3 py-1.5 bg-gray-100 text-[#475569] rounded-lg text-xs font-medium hover:bg-gray-200 flex items-center gap-1">
                          <Edit3 size={12} /> Edit
                        </button>
                        <button onClick={() => handleDelete(item.id)}
                          className="px-3 py-1.5 bg-red-50 text-red-500 rounded-lg text-xs font-medium hover:bg-red-100 flex items-center gap-1">
                          <Trash2 size={12} /> Delete
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Help note */}
      <div className="bg-[#FFFAF5] rounded-xl p-4 border border-[#f0e6d9]">
        <p className="text-sm text-[#475569]">
          <strong className="text-[#1E1B4B]">How this works:</strong> The bot loads all active knowledge items into its memory before every conversation. 
          FAQs are answered <strong>exactly as written</strong>. Info & Process items give the bot context to answer related questions accurately.
          Changes take effect within 5 minutes (no deployment needed).
        </p>
      </div>
    </div>
  );
};

export default BotTrainingTab;
